import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../../engine/api/hooks';
import type { FieldMeta } from '../../engine/meta/types';

// Typed reads for the composite project endpoints (gantt/burn/plan/workload) plus the small
// helpers every projects view shares. All data comes through the engine client.

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'review' | 'done' | 'cancelled';
export type DepType = 'fs' | 'ss' | 'ff' | 'sf';

export interface ProjectRow {
  id: string;
  ref: string;
  name: string;
  description: string | null;
  client_id: string | null;
  site_id: string | null;
  contact_id: string | null;
  contract_id: string | null;
  owner_tech_id: string | null;
  status: 'planned' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  priority: string;
  health: 'on_track' | 'at_risk' | 'off_track' | null;
  start_date: string | null;
  target_end_date: string | null;
  actual_end_date: string | null;
  progress_percent: string | null;
  template_id: string | null;
  currency: string;
  archived_at: string | null;
  budget_warning_level: number;
  source: Record<string, unknown>;
}

export interface PhaseRow {
  id: string;
  project_id: string;
  name: string;
  order: number;
  start_date: string | null;
  end_date: string | null;
  colour: string | null;
}

export interface TaskRow {
  id: string;
  project_id: string;
  phase_id: string | null;
  parent_task_id: string | null;
  task_no: number;
  ref: string;
  title: string;
  description: string | null;
  kind: 'task' | 'milestone' | 'approval' | 'issue';
  status: TaskStatus;
  priority: string;
  assignee_tech_id: string | null;
  start_date: string | null;
  due_date: string | null;
  estimate_minutes: number;
  actual_minutes: number;
  sort_key: string;
  weight: string;
  is_billable: boolean;
  labour_type_id: string | null;
  ticket_id: string | null;
  template_key: string | null;
  completed_at: string | null;
  portal_access: string;
  assignee_ids: string[];
  phase_name: string | null;
  assignee_name: string | null;
}

export interface DependencyRow {
  id: string;
  predecessor_id: string;
  successor_id: string;
  type: DepType;
  lag_days: number;
}

export interface ProgressRow {
  total_tasks: string;
  done_tasks: string;
  open_tasks: string;
  overdue_tasks: string;
  estimate_minutes: string;
  actual_minutes: string;
  progress_percent: string;
}

export interface MemberRow {
  id: string;
  tech_id: string;
  role: string;
  tech_name: string | null;
}

export interface ProjectDetail {
  project: ProjectRow;
  phases: PhaseRow[];
  tasks: TaskRow[];
  dependencies: DependencyRow[];
  progress: ProgressRow | null;
  members: MemberRow[];
}

export interface BurnRow {
  budget_id: string;
  budget_type: 'labour' | 'expense' | 'product' | 'total';
  planned_minutes: number;
  planned_minor: string;
  currency: string;
  actual_minutes: string;
  actual_minor: string;
  actual_cost_minor: string;
  burn_percent: string;
}

export interface BurnReport {
  project_id: string;
  budgets: BurnRow[];
  summary: { burn_percent: number; planned_minor: string; actual_minor: string; actual_minutes: number };
  progress: ProgressRow | null;
  snapshots: { taken_on: string; burn_percent: string; actual_minor: string }[];
  warning_level: number;
}

export interface PlanRow {
  id: string;
  key: string;
  title: string;
  kind: string;
  estimate_minutes: number;
  rule: string;
  depends_on: { key: string; type?: DepType; lag_days?: number }[];
}

export interface WorkloadCell {
  week_start: string;
  allocated_minutes: number;
  capacity_minutes: number;
  task_count: number;
  load: number;
}

export interface WorkloadTech {
  id: string;
  name: string;
  weekly_capacity_minutes: number;
  weeks: WorkloadCell[];
  total_allocated_minutes: number;
}

export interface WorkloadReport {
  from: string;
  to: string;
  weeks: string[];
  technicians: WorkloadTech[];
}

export interface TimeEntryRow {
  id: string;
  occurred_on: string;
  minutes: number;
  note: string | null;
  billable: boolean;
  charge_type: string;
  technician_id: string;
  technician_name: string | null;
  billable_amount_minor: string;
}

export const detailKey = (id: string) => ['projects', 'detail', id] as const;
export const burnKey = (id: string) => ['projects', 'burn', id] as const;
export const planKey = (id: string) => ['projects', 'plan', id] as const;
export const taskTimeKey = (id: string) => ['project-tasks', 'time', id] as const;

export function useProjectDetail(id: string | undefined) {
  const qc = useQueryClient();
  const q = useApiQuery<ProjectDetail>(detailKey(id ?? ''), `/v1/projects/${id ?? ''}/gantt`, { enabled: Boolean(id) });
  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: detailKey(id ?? '') });
    void qc.invalidateQueries({ queryKey: burnKey(id ?? '') });
    void qc.invalidateQueries({ queryKey: planKey(id ?? '') });
  }, [qc, id]);
  return { ...q, refresh };
}

export function useBurn(id: string | undefined) {
  return useApiQuery<BurnReport>(burnKey(id ?? ''), `/v1/projects/${id ?? ''}/burn`, { enabled: Boolean(id) });
}

export function usePlan(id: string | undefined, enabled = true) {
  return useApiQuery<{ data: PlanRow[] }>(planKey(id ?? ''), `/v1/projects/${id ?? ''}/plan`, { enabled: Boolean(id) && enabled });
}

export function useWorkload(from: string, to: string, techId?: string | null) {
  return useApiQuery<WorkloadReport>(['workload', from, to, techId ?? ''], '/v1/workload', { query: { from, to, tech_id: techId ?? undefined } });
}

/** `date` columns arrive as ISO timestamps; every picker/comparison wants the calendar day. */
export function day(v: unknown): string | null {
  if (v == null || v === '') return null;
  return String(v).slice(0, 10);
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Monday of the ISO week holding `iso`. */
export function weekStart(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return addDays(iso, -((d.getUTCDay() + 6) % 7));
}

export type ViewTone = 'accent' | 'pos' | 'warn' | 'neg' | 'muted';
export const STATUS_TONE: Record<TaskStatus, ViewTone> = { todo: 'muted', in_progress: 'accent', blocked: 'warn', review: 'accent', done: 'pos', cancelled: 'neg' };
export const DEP_LABEL: Record<DepType, string> = { fs: 'finish → start', ss: 'start → start', ff: 'finish → finish', sf: 'start → finish' };

/** Depth of a task in its subtask tree (0 = top level). */
export function depthOf(task: TaskRow, byId: Map<string, TaskRow>): number {
  let d = 0;
  let cur = task.parent_task_id;
  while (cur && d < 6) {
    d++;
    cur = byId.get(cur)?.parent_task_id ?? null;
  }
  return d;
}

/** Task progress for bars: done = 1, else logged / estimate. */
export function taskProgress(t: TaskRow): number {
  if (t.status === 'done') return 1;
  if (!t.estimate_minutes) return 0;
  return Math.min(1, t.actual_minutes / t.estimate_minutes);
}

/** A synthetic relation field for pickers outside a resource form (technician, ticket …). */
export function relField(name: string, label: string, resource: string, displayField = 'name'): FieldMeta {
  return { name, label, type: 'relation', relation: { resource, displayField }, required: false, readOnly: false, nullable: true, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: true, inList: true, inDetail: true };
}

export function durationField(name: string, label: string): FieldMeta {
  return { name, label, type: 'duration', required: false, readOnly: false, nullable: true, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: true, inList: true, inDetail: true };
}
